import { useState } from "react";
import api from "../../api/axios";
import { Bar, Banner, KIND, Ring, Section, Stat, dayKey, dayLabel, digits, errText, fmt, monthLabel } from "./cabinet-ui";

/* ───────── Обзор ───────── */

function PlanBlock({ sheet }: { sheet: any }) {
  const p = sheet.plan;
  const hasE = p.earnings > 0, hasD = p.deals > 0;
  if (!hasE && !hasD) {
    return (
      <Section title="План на месяц">
        <div className="cb-empty">План не задан.{sheet.canManage ? " Задайте его в разделе «Сотрудники»." : " Его задаёт руководитель."}</div>
      </Section>
    );
  }
  return (
    <Section title="План на месяц">
      <div className="cb-rings">
        {hasE && (
          <div>
            <Ring pct={p.earningsPct ?? 0} label="Заработок" sub={`${fmt(sheet.totals.earned)} из ${fmt(p.earnings)}`} />
            <div className="cb-note">
              {p.earningsPct >= 100 ? <span className="ok">🎉 План выполнен</span> : (
                <>
                  Осталось {fmt(p.earningsLeft)}
                  {p.daysLeft > 0 && p.perDayNeeded > 0 && <> · нужно в день {fmt(p.perDayNeeded)} ({p.daysLeft} дн.)</>}
                  {p.daysLeft > 0 && <> · при текущем темпе: {fmt(p.forecast)} ({Math.round((p.forecast / p.earnings) * 100)}%)</>}
                </>
              )}
            </div>
          </div>
        )}
        {hasD && (
          <div>
            <Ring pct={p.dealsPct ?? 0} label="Сделки" sub={`${sheet.earnings.dealsCount} из ${p.deals}`} />
            <div className="cb-note">
              {p.dealsPct >= 100 ? <span className="ok">🎉 План по сделкам выполнен</span> : <>Осталось сделок: {Math.max(0, p.deals - sheet.earnings.dealsCount)}</>}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function DaysChart({ days }: { days: any[] }) {
  if (!days.length) return null;
  const list = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const max = Math.max(...list.map((d) => d.total), 1);
  return (
    <Section title="Заработок по дням">
      <div className="cb-chart" role="img" aria-label="Заработок по дням месяца">
        {list.map((d) => (
          <div key={d.date} className="cb-chart-col" title={`${dayLabel(d.date)}: ${fmt(d.total)}, сделок ${d.dealsCount}`}>
            <div className="cb-chart-bar" style={{ height: `${Math.max(4, (d.total / max) * 100)}%` }} />
            <div className="cb-chart-day">{Number(d.date.slice(8))}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function OverviewTab({ sheet }: { sheet: any }) {
  const t = sheet.totals;
  const recent = sheet.earnings.deals.slice(0, 5);
  return (
    <>
      <div className="cb-hero">
        <div>
          <div className="cb-hero-label">К выплате за {monthLabel(sheet.month)}</div>
          <div className="cb-hero-value">{fmt(t.toPay)}</div>
          <div className="cb-hero-sub">
            Начислено {fmt(t.accrued)} − выплачено {fmt(t.paid)}
          </div>
        </div>
      </div>
      <div className="cb-stats">
        <Stat label="Заработано с продаж" value={fmt(t.earned)} tone="accent" />
        <Stat label="Оклад" value={fmt(t.base)} />
        <Stat label="Премии / штрафы" value={`${fmt(t.bonus)} / ${t.fine ? "−" + fmt(t.fine) : fmt(0)}`} />
        <Stat label="Сделок закрыто" value={String(sheet.earnings.dealsCount)} />
      </div>
      <PlanBlock sheet={sheet} />
      <DaysChart days={sheet.earnings.days} />
      {recent.length > 0 && (
        <Section title="Последние сделки">
          <div className="cb-list">
            {recent.map((d: any, i: number) => (
              <div key={d.dealId + "-" + i} className="cb-row">
                <div>
                  <b>{d.number}</b>
                  <div className="cb-sub">{d.client && d.client !== "—" ? d.client : "Без клиента"} · {new Date(d.doneAt).toLocaleDateString("ru-RU")}</div>
                </div>
                <b>{fmt(d.payout)}</b>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

/* ───────── Заработок ───────── */

export function EarningsTab({ sheet }: { sheet: any }) {
  const e = sheet.earnings;
  if (!e.deals.length) return <div className="cb-empty">За {monthLabel(sheet.month)} нет завершённых сделок</div>;
  const byDay = new Map<string, any[]>();
  for (const d of e.deals) {
    const k = dayKey(d.doneAt);
    byDay.set(k, [...(byDay.get(k) ?? []), d]);
  }
  const groups = Array.from(byDay.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  return (
    <>
      <div className="cb-stats">
        <Stat label="Заработок за месяц" value={fmt(e.total)} tone="accent" />
        <Stat label="Сделок" value={String(e.dealsCount)} />
        <Stat label="Средний заработок со сделки" value={fmt(e.dealsCount ? e.total / e.dealsCount : 0)} />
      </div>
      {groups.map(([day, deals]) => (
        <div key={day} className="cb-day">
          <div className="cb-day-head">
            <span>{dayLabel(day)}</span>
            <b>{fmt(deals.reduce((s, d) => s + d.payout, 0))}</b>
          </div>
          <div className="cb-list">
            {deals.map((d, i) => (
              <div key={d.dealId + "-" + i} className="cb-row">
                <div>
                  <b>{d.number}</b>
                  <div className="cb-sub">{d.client && d.client !== "—" ? d.client : "Без клиента"} · {d.role === "co" ? "соисполнитель" : "ответственный"}</div>
                </div>
                <b>{fmt(d.payout)}</b>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/* ───────── Зарплата ───────── */

export function SalaryTab({ sheet, onChanged }: { sheet: any; onChanged: () => void }) {
  const t = sheet.totals;
  const [err, setErr] = useState("");
  const del = async (id: number) => {
    if (!window.confirm("Удалить запись? Итоги месяца пересчитаются.")) return;
    setErr("");
    try { await api.delete(`/payroll/entries/${id}`); onChanged(); } catch (e) { setErr(errText(e)); onChanged(); }
  };
  return (
    <>
      {err && <Banner kind="err" onClose={() => setErr("")}>{err}</Banner>}
      <div className="cb-sheet">
        <div className="cb-sheet-row"><span>Оклад</span><b>{fmt(t.base)}</b></div>
        <div className="cb-sheet-row"><span>Проценты с продаж</span><b>{fmt(t.earned)}</b></div>
        <div className="cb-sheet-row"><span>Премии</span><b>{fmt(t.bonus)}</b></div>
        <div className="cb-sheet-row"><span>Штрафы</span><b>{t.fine ? "−" + fmt(t.fine) : fmt(0)}</b></div>
        <div className="cb-sheet-row total"><span>Начислено</span><b>{fmt(t.accrued)}</b></div>
        <div className="cb-sheet-row"><span>Выплачено (авансы и выплаты)</span><b>{t.paid ? "−" + fmt(t.paid) : fmt(0)}</b></div>
        <div className="cb-sheet-row total accent"><span>К выплате</span><b>{fmt(t.toPay)}</b></div>
      </div>

      <Section title="Записи за месяц">
        {sheet.entries.length === 0 ? (
          <div className="cb-empty">Премий, штрафов и выплат за этот месяц нет</div>
        ) : (
          <div className="cb-list">
            {sheet.entries.map((e: any) => (
              <div key={e.id} className="cb-row">
                <div>
                  <span className={`cb-kind ${e.kind}`}>{KIND[e.kind] ?? e.kind}</span>
                  <div className="cb-sub">{new Date(e.createdAt).toLocaleDateString("ru-RU")}{e.note ? ` · ${e.note}` : ""}{e.createdByName ? ` · внёс: ${e.createdByName}` : ""}</div>
                </div>
                <div className="cb-row-right">
                  <b>{e.kind === "fine" ? "−" : ""}{fmt(e.amount)}</b>
                  {sheet.canManage && <button type="button" className="cb-link danger" onClick={() => del(e.id)}>Удалить</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {sheet.canManage && <EntryForm userId={sheet.user.id} month={sheet.month} toPay={t.toPay} onDone={onChanged} />}
    </>
  );
}

function EntryForm({ userId, month, toPay, onDone }: { userId: number; month: string; toPay: number; onDone: () => void }) {
  const [kind, setKind] = useState("bonus");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    const n = Number(amount);
    if (!n || n <= 0) { setErr("Укажите сумму больше нуля"); return; }
    if ((kind === "payout" || kind === "advance") && n > Math.max(0, toPay)) {
      if (!window.confirm(`Сумма ${fmt(n)} больше, чем к выплате (${fmt(Math.max(0, toPay))}). Всё равно внести?`)) return;
    }
    setSaving(true);
    try {
      await api.post("/payroll/entries", { userId, month, kind, amount: n, note: note.trim() });
      setAmount(""); setNote("");
      onDone();
    } catch (e) { setErr(errText(e)); } finally { setSaving(false); }
  };
  return (
    <Section title={`Добавить запись за ${monthLabel(month)}`}>
      <div className="cb-form">
        {err && <Banner kind="err" onClose={() => setErr("")}>{err}</Banner>}
        <div className="cb-form-row">
          <label>Вид<select value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
          <label>Сумма, ₸<input type="text" inputMode="numeric" value={amount} onChange={(e) => setAmount(digits(e.target.value))} placeholder="0" /></label>
          <label className="grow">Комментарий<input type="text" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="За что" /></label>
          <button type="button" className="cb-btn" onClick={submit} disabled={saving}>{saving ? "…" : "Добавить"}</button>
        </div>
      </div>
    </Section>
  );
}

export { Bar };
